import type { Filter, MetricSelection } from "@cd/core";
import { draftToDefinition, type Draft } from "./store";

/** Labels for a pack's fields, by ref. */
export type LabelOf = (ref: string) => string;

const cap = (s: string) => s.charAt(0).toUpperCase() + s.slice(1).replace(/_/g, " ");
const OPS: Record<Filter["op"], string> = {
  eq: "is", neq: "is not", in: "is", not_in: "is not", gt: ">", gte: "≥", lt: "<", lte: "≤", between: "between",
  contains: "contains", is_null: "is empty", not_null: "is not empty",
};
const COMPARE = { previous_period: "vs previous period", previous_year: "vs last year" } as const;

const metricKey = (m: MetricSelection) => (m.kind === "metric" ? m.ref : `${m.id}=${m.expr}`);
const metricLabel = (m: MetricSelection, labelOf: LabelOf) => (m.kind === "metric" ? labelOf(m.ref) : m.label);
const filterKey = (f: Filter) => JSON.stringify([f.ref, f.op, f.values]);
const filterLabel = (f: Filter, labelOf: LabelOf) =>
  `${labelOf(f.ref)} ${OPS[f.op]}${f.op === "is_null" || f.op === "not_null" ? "" : ` ${f.values.join(f.op === "between" ? " and " : ", ")}`}`;

/** What each list gained ("+ …") and lost ("− …"), keyed so the same item counts as kept. */
function listChanges<T>(before: T[], after: T[], key: (x: T) => string, label: (x: T) => string): string[] {
  const had = new Set(before.map(key));
  const has = new Set(after.map(key));
  return [
    ...after.filter((x) => !had.has(key(x))).map((x) => `+ ${label(x)}`),
    ...before.filter((x) => !has.has(key(x))).map((x) => `− ${label(x)}`),
  ];
}

/**
 * What an Ask edit changed, as short chips ("Day → Week", "+ Area is Patio", "+ vs last year",
 * "Bar → Line"), worked out by comparing the drafts here rather than trusting the model's account.
 */
export function askChanges(before: Draft, after: Draft, labelOf: LabelOf): string[] {
  const chips: string[] = [];
  if (before.viz.type !== after.viz.type) chips.push(`${cap(before.viz.type)} → ${cap(after.viz.type)}`);
  chips.push(...listChanges(before.metrics, after.metrics, metricKey, (m) => metricLabel(m, labelOf)));

  // A grouping kept with another grain is a change of grain, not a swap.
  const regrained = after.dimensions.filter((d) => before.dimensions.some((b) => b.ref === d.ref && b.grain !== d.grain));
  for (const d of regrained) {
    const was = before.dimensions.find((b) => b.ref === d.ref)!;
    chips.push(was.grain && d.grain ? `${cap(was.grain)} → ${cap(d.grain)}` : `${labelOf(d.ref)}: ${d.grain ? cap(d.grain) : "no grain"}`);
  }
  const dimKey = (d: Draft["dimensions"][number]) => d.ref;
  chips.push(...listChanges(before.dimensions, after.dimensions, dimKey, (d) => labelOf(d.ref)));
  // Order matters (a pivot's rows come before its columns), so the same groupings in another order is a change.
  const kept = (a: Draft, b: Draft) => a.dimensions.filter((d) => b.dimensions.some((x) => x.ref === d.ref)).map(dimKey).join();
  if (kept(before, after) !== kept(after, before)) chips.push("Groupings reordered");

  chips.push(...listChanges(before.filters, after.filters, filterKey, (f) => filterLabel(f, labelOf)));

  if (before.compareTo !== after.compareTo) {
    chips.push(!after.compareTo ? `− ${COMPARE[before.compareTo!]}` : !before.compareTo ? `+ ${COMPARE[after.compareTo]}` : `${COMPARE[before.compareTo]} → ${COMPARE[after.compareTo]}`);
  } else if (after.compareTo && (before.comparePeriods ?? 1) !== (after.comparePeriods ?? 1)) {
    chips.push(`${after.comparePeriods ?? 1} period${(after.comparePeriods ?? 1) === 1 ? "" : "s"} back`);
  }
  if (before.averagePer !== after.averagePer) chips.push(after.averagePer ? "+ Average per day" : "− Average per day");
  if (before.limit !== after.limit) chips.push(after.limit ? `Top ${after.limit}` : "− Top N");
  const sortOf = (d: Draft) => d.sort.map((s) => `${s.key}:${s.dir}`).join();
  if (sortOf(before) !== sortOf(after)) {
    const s = after.sort[0];
    const metric = s && after.metrics.find((m) => (m.kind === "metric" ? m.ref : m.id) === s.key);
    chips.push(!s ? "− Sort" : `Sorted by ${metric ? metricLabel(metric, labelOf) : labelOf(s.key)} ${s.dir === "desc" ? "↓" : "↑"}`);
  }
  if (before.title.trim() !== after.title.trim()) chips.push(`Renamed “${after.title.trim()}”`);
  // Anything the chips above don't name still counts: an empty list must mean the widget is the same.
  if (!chips.length && JSON.stringify(draftToDefinition(before)) !== JSON.stringify(draftToDefinition(after))) chips.push("Other settings changed");
  return chips;
}
